import { useEffect, useRef, useState } from "react";
import CustomSelect from "../../../../custom_components/CustomSelect";
import useModals from "../../../../hooks/useModals";
import useMonitor from "../../../../hooks/useMonitor";
import useScenario from "../../../../hooks/useScenario";
import useUrlParams from "../../../../hooks/useUrlParams";
import { Modals } from "../../../../types";
import ADCS from "../../../grafana_panels/ADCS";
import Beacon from "../../../grafana_panels/Beacon";
import Communication from "../../../grafana_panels/Communication";
import EPS from "../../../grafana_panels/EPS";
import OBC from "../../../grafana_panels/OBC";
import Payload from "../../../grafana_panels/Payload";
import Propulsion from "../../../grafana_panels/Propulsion";
import { get_monitor_button_list } from "../../monitor/utils";
import Modal from "../Modal";
import { CollapseSvg, PinSvg, TelemetrySvgWhite } from "../Svgs";
import './Telemetry.scss';
import TelemetryPanels from "./TelemetryPanels";
import useTelemetry from "../../telemetry/hooks/useTelemetry";
import { TelemetryNotFound } from "../../telemetry/components";

const Telemetry = () => {

    const [expand, setExpand] = useState(false);
    const { scenario } = useScenario()
    const { dashboard } = useUrlParams()
    const { scenarioDeploymentDetails } = useTelemetry()
    const { panels, selected_panel, change_selected_panel, get_updated_url } = useMonitor()
    const { open_modal, close_modal, pinned_modal, get_modal_details, close_all_unpinned_modals } = useModals()


    const modalContainerRef = useRef<HTMLDivElement>(null);
    const modal: Modals = get_modal_details('telemetry')

    const showTelemetry = modal.open && scenarioDeploymentDetails?.dashboardVersion === '2.0.0'
    const showGrafana = modal.open && scenarioDeploymentDetails?.dashboardVersion === '1.0.0'
    const telemetryDashboardNotFound = modal.open && !showTelemetry && !showGrafana


    useEffect(() => {
        const close_open_modal = (event: Event) => {
            const target = event.target;
            if (target instanceof Element) {
                // If element was unmounted during click handler execution (e.g., closing menu), don't close modal
                if (!target.isConnected) return;

                // Don't close if clicking inside the telemetry container
                if (modalContainerRef.current?.contains(target)) return;

                // Don't close if clicking inside telemetry floating menus, backdrops or overlays
                if (target.closest('.td-floating-menu, .td-floating-menu-backdrop, .telemetry-modal-overlay, .telemetry-modal-container')) return;
            }

            const currentModal = get_modal_details('telemetry');
            // Close telemetry if it's open and NOT pinned
            if (currentModal.open && !currentModal.pinned) {
                close_modal('telemetry');
            }
        };
        // Use capture phase to ensure this fires before other handlers
        document.addEventListener('click', close_open_modal, true);
        return () => {
            document.removeEventListener('click', close_open_modal, true)
        }
    }, [get_modal_details, close_modal])

    return (
        <Modal
            open={modal.open}
            pinned={modal.current_modal === 'telemetry' && modal.pinned}
            disabled={modal.current_modal === 'telemetry' && modal.pinned}
            class_name={`${modal.current_modal === 'telemetry' ? 'current__modal' : ''}`}
        >
            <div
                ref={modalContainerRef}
                className={`telemetry__panel__container ${expand && 'expand'} `}
                onClick={(event: any) => {
                    event.stopPropagation()
                    open_modal('telemetry')
                }}>
                <div className="telemetry__panel__header">
                    <div className=" d-flex align-items-center gap-2">
                        <TelemetrySvgWhite />
                        <span className="panel__header__title">Telemetry</span>
                    </div>
                    <div className=" d-flex align-items-center">
                        <button className="panel__colapse__btn" onClick={(event: any) => {
                            event.stopPropagation()
                            setExpand(!expand)
                        }}>
                            <CollapseSvg />
                        </button>
                        <button className={`panel__pin__btn ${modal.pinned ? 'pinned' : ''}`}
                            onClick={(event: any) => {
                                event.stopPropagation()
                                pinned_modal('telemetry')

                            }}>
                            <PinSvg />
                        </button>
                        <button className={`panel__close__btn`} onClick={(event: any) => {
                            event.stopPropagation()
                            close_modal('telemetry')
                        }}>
                            <i className="fe fe-x fs-16" />
                        </button>
                    </div>
                </div>

                {telemetryDashboardNotFound &&
                    <div className="telemetry__panel__body__container" >
                        <TelemetryNotFound
                            message='Telemetry Panels Not Found'
                            description='Telemetry panel configuration or parameter catalog API data could not be loaded for this satellite.'
                        />
                    </div>
                }
                {showGrafana &&
                    <div className="telemetry__panel__body__container" >
                        <div className="telemetry__panel__body__container__header">
                            <CustomSelect
                                className="header__select__container"
                                options={get_monitor_button_list(scenario, dashboard)}
                                disabled_search={true}
                                value={{ label: selected_panel, value: selected_panel }}
                                onChange={(data) => { change_selected_panel(data.label) }}
                            />
                        </div>
                        <div className="telemetry__panel__body__container__body">
                            {selected_panel === 'Beacon' && <Beacon monitor_view_type={'List'} panels={panels ? panels : {}} get_updated_url={get_updated_url} />}
                            {selected_panel === 'EPS' && <EPS monitor_view_type={'List'} panels={panels ? panels : []} get_updated_url={get_updated_url} />}
                            {selected_panel === 'OBC' && <OBC monitor_view_type={'List'} panels={panels ? panels : []} get_updated_url={get_updated_url} />}
                            {selected_panel === 'ADCS' && <ADCS monitor_view_type={'List'} panels={panels ? panels : {}} get_updated_url={get_updated_url} />}
                            {selected_panel === 'Communication' && <Communication monitor_view_type={'List'} panels={panels ? panels : {}} get_updated_url={get_updated_url} />}
                            {selected_panel === 'Propulsion' && <Propulsion monitor_view_type={'List'} panels={panels ? panels : []} get_updated_url={get_updated_url} />}
                            {selected_panel === 'Payload' && <Payload monitor_view_type={'List'} panels={panels ? panels : {}} get_updated_url={get_updated_url} />}
                        </div>
                    </div>
                }
                {showTelemetry && <TelemetryPanels />}
            </div>
        </Modal>
    )
}

export default Telemetry